import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { lookupPublicRanking } from "@/lib/public-rankings.functions";
import {
  CARD_SUPPORT_TEXT,
  COMPANY_SECTOR_OPTIONS,
  COMPANY_STAGE_OPTIONS,
  FUND_PROFILE_OPTIONS,
  LOAN_OPTIONS,
  NO_LIST_TEXT,
  RE_DEBT_OPTIONS,
  RE_EQUITY_OPTIONS,
  SEEDER_HELPER_TEXT,
  TRIAL_COPY,
  categoryOptions,
  companyResultHeading,
  companyStagesForSector,
  findCompanyPageSlug,
  trialSearchParams,
  type Option,
} from "@/lib/top10-finder";
import {
  buildHandoff,
  saveHandoff,
  type FinderAnswers,
  type FinderFirm,
} from "@/lib/finder-handoff";

export type FinderCardId = "company" | "real_estate" | "fund" | "business_loan";

type LookupResult = Awaited<ReturnType<typeof lookupPublicRanking>>["list"];

type Props = {
  cardId: FinderCardId;
  buttonLabel: string;
  open: boolean;
  onToggle: (id: FinderCardId) => void;
};

const PANEL_ID = (id: string) => `finder-panel-${id}`;

/** One card's expandable Top 10 finder: selections, results, trial action. */
export function Top10FinderCard({ cardId, buttonLabel, open, onToggle }: Props) {
  const lookup = useServerFn(lookupPublicRanking);

  const [stage, setStage] = React.useState("");
  const [sector, setSector] = React.useState("");
  const [capitalType, setCapitalType] = React.useState<"equity" | "debt">("equity");
  const [propertyType, setPropertyType] = React.useState("");
  const [fundProfile, setFundProfile] = React.useState("funds_established");
  const [fundStrategy, setFundStrategy] = React.useState("");
  const [loanType, setLoanType] = React.useState("");

  const [list, setList] = React.useState<LookupResult>(null);
  const [status, setStatus] = React.useState<"idle" | "loading" | "done" | "error" | "limited">(
    "idle",
  );

  // Closing a card clears its selections and results so a reopened card never
  // shows a stale answer.
  React.useEffect(() => {
    if (open) return;
    setStage("");
    setSector("");
    setCapitalType("equity");
    setPropertyType("");
    setFundProfile("funds_established");
    setFundStrategy("");
    setLoanType("");
    setList(null);
    setStatus("idle");
  }, [open]);

  const propertyOptions = capitalType === "equity" ? RE_EQUITY_OPTIONS : RE_DEBT_OPTIONS;
  const strategyOptions = React.useMemo(
    () => categoryOptions(fundProfile as "funds_established"),
    [fundProfile],
  );
  const stageOptions: Option[] = React.useMemo(() => {
    if (!sector) return COMPANY_STAGE_OPTIONS;
    const allowed = new Set(companyStagesForSector(sector));
    return COMPANY_STAGE_OPTIONS.filter((o) => allowed.has(o.value));
  }, [sector]);

  const ready =
    cardId === "company"
      ? Boolean(stage && sector)
      : cardId === "real_estate"
        ? Boolean(propertyType)
        : cardId === "fund"
          ? Boolean(fundStrategy)
          : Boolean(loanType);

  const submitLabel =
    cardId === "company" || cardId === "fund"
      ? "Show Top 10 Investors"
      : cardId === "real_estate"
        ? capitalType === "equity"
          ? "Show Top 10 Investors"
          : "Show Top 10 Lenders"
        : "Show Top 10 Lenders";

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setStatus("loading");
    setList(null);
    try {
      const input =
        cardId === "company"
          ? ({ kind: "company", stage, category: sector } as const)
          : cardId === "real_estate"
            ? ({
                kind: "provider",
                groupKey: capitalType === "equity" ? "re_equity" : "re_debt",
                categoryKey: propertyType,
              } as const)
            : cardId === "fund"
              ? ({ kind: "provider", groupKey: fundProfile, categoryKey: fundStrategy } as const)
              : ({ kind: "provider", groupKey: "loans", categoryKey: loanType } as const);
      const res = await lookup({ data: input });
      if (!res.ok) {
        setStatus("limited");
        return;
      }
      setList(res.list);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }

  const answers = {
    company_stage: cardId === "company" ? stage : undefined,
    company_sector: cardId === "company" ? sector : undefined,
    re_capital_type: cardId === "real_estate" ? capitalType : undefined,
    property_type: cardId === "real_estate" ? propertyType : undefined,
    fund_profile: cardId === "fund" ? fundProfile : undefined,
    fund_strategy: cardId === "fund" ? fundStrategy : undefined,
    loan_type: cardId === "business_loan" ? loanType : undefined,
  };
  const trialSearch = trialSearchParams(cardId, answers);

  // Step B: once results are on screen, keep the selections in local storage
  // under an opaque token and carry only the token into signup.
  const [handoffToken, setHandoffToken] = React.useState<string | null>(null);
  const answersKey = JSON.stringify(answers);
  // The firms on screen travel with the selections, so the first delivery
  // contains the firms the visitor came for. Provider rows carry the
  // directory domain; company rows carry a name only and are resolved by
  // name when the deal is created.
  const firmsKey = JSON.stringify(
    list?.kind === "provider"
      ? list.rows.map((r) => ({
          name: r.firm_name,
          domain: r.directory_firm_id ?? "",
        }))
      : list?.kind === "company"
        ? list.rows.map((r) => ({ name: r.name, domain: "" }))
        : [],
  );
  React.useEffect(() => {
    if (status !== "done") {
      setHandoffToken(null);
      return;
    }
    const handoff = buildHandoff(
      cardId,
      JSON.parse(answersKey) as FinderAnswers,
      new Date(),
      JSON.parse(firmsKey) as FinderFirm[],
    );
    setHandoffToken(handoff ? saveHandoff(handoff) : null);
  }, [status, answersKey, firmsKey, cardId]);

  return (
    <div className="finder">
      <button
        type="button"
        className="finder-toggle"
        aria-expanded={open}
        aria-controls={PANEL_ID(cardId)}
        onClick={() => onToggle(cardId)}
      >
        {buttonLabel}
      </button>
      <p className="finder-support">{CARD_SUPPORT_TEXT}</p>

      {open ? (
        <div className="finder-panel" id={PANEL_ID(cardId)}>
          <form onSubmit={onSubmit}>
            {cardId === "company" ? (
              <>
                <Select
                  id={`${cardId}-sector`}
                  label="Sector"
                  value={sector}
                  onChange={(v) => {
                    setSector(v);
                    setList(null);
                    setStatus("idle");
                  }}
                  options={COMPANY_SECTOR_OPTIONS}
                />
                <Select
                  id={`${cardId}-stage`}
                  label="Company stage"
                  value={stage}
                  onChange={(v) => {
                    setStage(v);
                    setList(null);
                    setStatus("idle");
                  }}
                  options={stageOptions}
                />
              </>
            ) : null}

            {cardId === "real_estate" ? (
              <>
                <fieldset className="finder-choices">
                  <legend>Capital type</legend>
                  {(["equity", "debt"] as const).map((v) => (
                    <label key={v} className={capitalType === v ? "is-selected" : undefined}>
                      <input
                        type="radio"
                        name={`${cardId}-capital`}
                        value={v}
                        checked={capitalType === v}
                        onChange={() => {
                          setCapitalType(v);
                          setPropertyType("");
                          setList(null);
                          setStatus("idle");
                        }}
                      />
                      {v === "equity" ? "Equity" : "Debt"}
                    </label>
                  ))}
                </fieldset>
                <Select
                  id={`${cardId}-property`}
                  label="Property type"
                  value={propertyType}
                  onChange={(v) => {
                    setPropertyType(v);
                    setList(null);
                    setStatus("idle");
                  }}
                  options={propertyOptions}
                />
              </>
            ) : null}

            {cardId === "fund" ? (
              <>
                <Select
                  id={`${cardId}-profile`}
                  label="Manager profile"
                  value={fundProfile}
                  onChange={(v) => {
                    setFundProfile(v);
                    setFundStrategy("");
                    setList(null);
                    setStatus("idle");
                  }}
                  options={FUND_PROFILE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
                <p className="finder-help">{SEEDER_HELPER_TEXT}</p>
                <Select
                  id={`${cardId}-strategy`}
                  label="Strategy"
                  value={fundStrategy}
                  onChange={(v) => {
                    setFundStrategy(v);
                    setList(null);
                    setStatus("idle");
                  }}
                  options={strategyOptions}
                />
              </>
            ) : null}

            {cardId === "business_loan" ? (
              <Select
                id={`${cardId}-loan`}
                label="Financing type"
                value={loanType}
                onChange={(v) => {
                  setLoanType(v);
                  setList(null);
                  setStatus("idle");
                }}
                options={LOAN_OPTIONS}
              />
            ) : null}

            <button type="submit" className="finder-submit" disabled={!ready}>
              {submitLabel}
            </button>
          </form>

          <div className="finder-results" aria-live="polite">
            {status === "loading" ? <p className="finder-note">Loading the list.</p> : null}
            {status === "error" ? (
              <p className="finder-note">Something went wrong. Please try again.</p>
            ) : null}
            {status === "limited" ? (
              <p className="finder-note">
                You have reached today's lookup limit. Please try again tomorrow.
              </p>
            ) : null}
            {status === "done" && !list ? <p className="finder-note">{NO_LIST_TEXT}</p> : null}

            {status === "done" && list?.kind === "company" ? (
              <div>
                <h4>{companyResultHeading(stage, sector)}</h4>
                <ol className="finder-list">
                  {list.rows.map((row) => (
                    <li key={row.rank}>
                      <span className="finder-rank">{row.rank}</span>
                      <span className="finder-firm">{row.name}</span>
                    </li>
                  ))}
                </ol>
                {list.rows.length < 10 ? (
                  <p className="finder-note">
                    This list currently shows {list.rows.length} firms.
                  </p>
                ) : null}
                <p className="finder-note">{list.methodology}</p>
                {findCompanyPageSlug(stage, sector) ? (
                  <Link
                    className="finder-page-link"
                    to="/lists/$slug"
                    params={{ slug: findCompanyPageSlug(stage, sector)! }}
                  >
                    See the published page
                  </Link>
                ) : null}
              </div>
            ) : null}

            {status === "done" && list?.kind === "provider" ? (
              <div>
                <h4>{list.categoryLabel}</h4>
                <ol className="finder-list">
                  {list.rows.map((row) => (
                    <li key={`${row.rank}-${row.firm_name}`}>
                      <span className="finder-rank">{row.rank}</span>
                      <span className="finder-firm">
                        {row.firm_name}
                        {row.firm_type || row.hq ? (
                          <em>{[row.firm_type, row.hq].filter(Boolean).join(" · ")}</em>
                        ) : null}
                      </span>
                      {row.source !== "directory" ? (
                        <span className="finder-marker">{row.source}</span>
                      ) : null}
                    </li>
                  ))}
                </ol>
                {list.rows.length < 10 ? (
                  <p className="finder-note">
                    This list currently shows {list.rows.length} firms.
                  </p>
                ) : null}
                <p className="finder-note">{list.ruleText}</p>
                {list.directoryAsOf ? (
                  <p className="finder-note">Directory as of {list.directoryAsOf}</p>
                ) : null}
              </div>
            ) : null}

            {status === "done" ? (
              <div className="finder-trial">
                {/* Plain anchor: the typed Link drops the finder parameters. */}
                <a
                  href={`/auth?${new URLSearchParams({
                    ...(trialSearch as Record<string, string>),
                    ...(handoffToken ? { finder: handoffToken } : {}),
                  }).toString()}`}
                  className="finder-trial-button"
                >
                  Start my free trial
                </a>
                <p className="finder-note">{TRIAL_COPY}</p>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Select({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
}) {
  return (
    <div className="finder-field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
